(() => {
	'use strict';

	// Elements
	const el = {
		gpStatus: document.getElementById('gp-status'),
		gpName: document.getElementById('gp-name'),
		btnDetectAxis: document.getElementById('btn-detect-axis'),
		axisSelect: document.getElementById('axis-select'),
		btnSetReleased: document.getElementById('btn-set-released'),
		btnSetPressed: document.getElementById('btn-set-pressed'),
		rawValue: document.getElementById('raw-value'),
		calibratedPct: document.getElementById('calibrated-pct'),
		minVal: document.getElementById('min-val'),
		maxVal: document.getElementById('max-val'),
		gaugeFill: document.getElementById('gauge-fill'),
		gaugeTarget: document.getElementById('gauge-target'),
		gaugeLabel: document.getElementById('gauge-label'),
		targetLabel: document.getElementById('target-label'),
		targetRange: document.getElementById('target-range'),
		targetInput: document.getElementById('target-input'),
		hystRange: document.getElementById('hyst-range'),
		hystInput: document.getElementById('hyst-input'),
		volRange: document.getElementById('vol-range'),
		volInput: document.getElementById('vol-input'),
		freqInput: document.getElementById('freq-input'),
		durInput: document.getElementById('dur-input'),
		modeSelect: document.getElementById('mode-select'),
		btnTestTone: document.getElementById('btn-test-tone'),
		btnStart: document.getElementById('btn-start'),
		btnStop: document.getElementById('btn-stop'),
	};

	// State
	let gamepadIndex = null;
	let axisIndex = 0;
	let calibrationMin = -1;
	let calibrationMax = 1;
	let monitoring = false;
	let rafId = null;
	let axisDetecting = false;

	let audioCtx = null;
	let continuousOsc = null;
	let continuousGain = null;
	let armedForOneShot = true;

	// Settings (persisted)
	const settings = {
		targetPct: 50,
		hysteresisPct: 2,
		volume: 0.3,
		frequencyHz: 880,
		durationMs: 300,
		mode: 'oneshot', // or 'continuous'
	};

	// Utilities
	function clamp(value, min, max) {
		return Math.min(max, Math.max(min, value));
	}

	function pct(n) {
		return `${Math.round(n)}%`;
	}

	function loadSettings() {
		try {
			const raw = localStorage.getItem('brakeToneSettings');
			if (raw) {
				const obj = JSON.parse(raw);
				Object.assign(settings, obj);
			}
			const saved = localStorage.getItem('brakeToneCalibration');
			if (saved) {
				const c = JSON.parse(saved);
				if (typeof c.axisIndex === 'number') axisIndex = c.axisIndex;
				if (typeof c.min === 'number') calibrationMin = c.min;
				if (typeof c.max === 'number') calibrationMax = c.max;
			}
		} catch {}
	}

	function saveSettings() {
		localStorage.setItem('brakeToneSettings', JSON.stringify(settings));
		localStorage.setItem('brakeToneCalibration', JSON.stringify({
			axisIndex,
			min: calibrationMin,
			max: calibrationMax,
		}));
	}

	function initAudioContext() {
		if (!audioCtx) {
			audioCtx = new (window.AudioContext || window.webkitAudioContext)();
		}
		if (audioCtx.state === 'suspended') {
			audioCtx.resume();
		}
	}

	function oneShotBeep() {
		initAudioContext();
		const now = audioCtx.currentTime;
		const osc = audioCtx.createOscillator();
		const gain = audioCtx.createGain();
		osc.type = 'sine';
		osc.frequency.setValueAtTime(settings.frequencyHz, now);
		// small fade in/out to avoid clicks
		gain.gain.setValueAtTime(0, now);
		gain.gain.linearRampToValueAtTime(settings.volume, now + 0.01);
		const endTime = now + Math.max(0.05, settings.durationMs / 1000);
		gain.gain.linearRampToValueAtTime(0.0001, endTime);
		osc.connect(gain).connect(audioCtx.destination);
		osc.start(now);
		osc.stop(endTime + 0.02);
	}

	function ensureContinuousTone(on) {
		initAudioContext();
		const now = audioCtx.currentTime;
		if (on) {
			if (!continuousOsc) {
				continuousOsc = audioCtx.createOscillator();
				continuousGain = audioCtx.createGain();
				continuousOsc.type = 'sine';
				continuousOsc.frequency.setValueAtTime(settings.frequencyHz, now);
				continuousGain.gain.setValueAtTime(0, now);
				continuousOsc.connect(continuousGain).connect(audioCtx.destination);
				continuousOsc.start(now);
				continuousGain.gain.linearRampToValueAtTime(settings.volume, now + 0.02);
			} else {
				continuousOsc.frequency.setValueAtTime(settings.frequencyHz, now);
				continuousGain.gain.linearRampToValueAtTime(settings.volume, now + 0.05);
			}
		} else {
			if (continuousOsc) {
				continuousGain.gain.cancelScheduledValues(now);
				continuousGain.gain.setValueAtTime(continuousGain.gain.value, now);
				continuousGain.gain.linearRampToValueAtTime(0.0001, now + 0.03);
				const oscRef = continuousOsc;
				setTimeout(() => {
					try { oscRef.stop(); } catch {}
				}, 60);
				continuousOsc = null;
				continuousGain = null;
			}
		}
	}

	function updateUIFromSettings() {
		el.targetRange.value = String(settings.targetPct);
		el.targetInput.value = String(settings.targetPct);
		el.hystRange.value = String(settings.hysteresisPct);
		el.hystInput.value = String(settings.hysteresisPct);
		el.volRange.value = String(settings.volume);
		el.volInput.value = String(settings.volume);
		el.freqInput.value = String(settings.frequencyHz);
		el.durInput.value = String(settings.durationMs);
		el.modeSelect.value = settings.mode;
		el.targetLabel.textContent = `Target: ${settings.targetPct}%`;
		el.gaugeTarget.style.left = `${settings.targetPct}%`;
		el.minVal.textContent = `${calibrationMin.toFixed(3)}`;
		el.maxVal.textContent = `${calibrationMax.toFixed(3)}`;
	}

	function bindSettingInputs() {
		function linkRangeNumber(rangeEl, numberEl, onChange) {
			const handler = (src, dst) => () => {
				dst.value = src.value;
				onChange(parseFloat(src.value));
				saveSettings();
			};
			rangeEl.addEventListener('input', handler(rangeEl, numberEl));
			numberEl.addEventListener('input', handler(numberEl, rangeEl));
		}

		linkRangeNumber(el.targetRange, el.targetInput, (val) => {
			settings.targetPct = clamp(val, 0, 100);
			el.targetLabel.textContent = `Target: ${settings.targetPct}%`;
			el.gaugeTarget.style.left = `${settings.targetPct}%`;
			if (settings.mode === 'oneshot') {
				armedForOneShot = true;
			}
		});
		linkRangeNumber(el.hystRange, el.hystInput, (val) => {
			settings.hysteresisPct = clamp(val, 0, 20);
		});
		linkRangeNumber(el.volRange, el.volInput, (val) => {
			settings.volume = clamp(val, 0, 1);
		});

		el.freqInput.addEventListener('input', () => {
			settings.frequencyHz = clamp(parseFloat(el.freqInput.value || '0'), 100, 4000);
			saveSettings();
		});
		el.durInput.addEventListener('input', () => {
			settings.durationMs = clamp(parseFloat(el.durInput.value || '0'), 50, 2000);
			saveSettings();
		});
		el.modeSelect.addEventListener('change', () => {
			settings.mode = el.modeSelect.value;
			if (settings.mode === 'oneshot') {
				ensureContinuousTone(false);
				armedForOneShot = true;
			}
			saveSettings();
		});

		el.btnTestTone.addEventListener('click', () => {
			oneShotBeep();
		});
	}

	// Gamepad handling
	function onGamepadConnected(gp) {
		gamepadIndex = gp.index;
		el.gpStatus.textContent = 'Connected';
		el.gpName.textContent = `${gp.id} — ${gp.axes.length} axes, ${gp.buttons.length} buttons`;
		populateAxisOptions(gp.axes.length);
		setControlsEnabled(true);
	}

	function onGamepadDisconnected() {
		gamepadIndex = null;
		el.gpStatus.textContent = 'Disconnected';
		el.gpName.textContent = '';
		setControlsEnabled(false);
		ensureContinuousTone(false);
		monitoring = false;
		updateStartStopButtons();
	}

	function setControlsEnabled(connected) {
		el.btnDetectAxis.disabled = !connected;
		el.axisSelect.disabled = !connected;
		el.btnSetReleased.disabled = !connected;
		el.btnSetPressed.disabled = !connected;
		el.btnStart.disabled = !connected;
	}

	function populateAxisOptions(count) {
		el.axisSelect.innerHTML = '';
		for (let i = 0; i < count; i++) {
			const opt = document.createElement('option');
			opt.value = String(i);
			opt.textContent = `Axis ${i}`;
			el.axisSelect.appendChild(opt);
		}
		// Clamp saved axis index to available range
		if (axisIndex >= count) axisIndex = 0;
		el.axisSelect.value = String(axisIndex);
		el.axisSelect.addEventListener('change', () => {
			axisIndex = parseInt(el.axisSelect.value, 10);
			saveSettings();
		});
	}

	function getActiveGamepad() {
		const gps = navigator.getGamepads ? navigator.getGamepads() : [];
		const gp = gps && typeof gamepadIndex === 'number' ? gps[gamepadIndex] : null;
		return gp && gp.connected ? gp : null;
	}

	function getRawAxisValue() {
		const gp = getActiveGamepad();
		if (!gp) return null;
		if (!gp.axes || gp.axes.length === 0) return null;
		const idx = clamp(axisIndex, 0, gp.axes.length - 1);
		return gp.axes[idx];
	}

	function toCalibratedPercent(raw) {
		if (calibrationMax === calibrationMin) return 0;
		const t = (raw - calibrationMin) / (calibrationMax - calibrationMin);
		return clamp(t * 100, 0, 100);
	}

	function updateStartStopButtons() {
		el.btnStart.disabled = !getActiveGamepad() || monitoring;
		el.btnStop.disabled = !monitoring;
	}

	// Axis detection
	function detectAxis() {
		if (!getActiveGamepad() || axisDetecting) return;
		axisDetecting = true;
		el.btnDetectAxis.textContent = 'Move the brake… (2s)';

		const start = performance.now();
		const duration = 2000;
		const base = (getActiveGamepad().axes || []).slice();
		const deltas = new Array(base.length).fill(0);

		function frame() {
			const gp = getActiveGamepad();
			if (gp && gp.axes) {
				for (let i = 0; i < gp.axes.length; i++) {
					const diff = Math.abs(gp.axes[i] - (base[i] || 0));
					if (diff > deltas[i]) deltas[i] = diff;
				}
			}
			if (performance.now() - start < duration) {
				requestAnimationFrame(frame);
			} else {
				let bestIdx = 0;
				let bestVal = -1;
				for (let i = 0; i < deltas.length; i++) {
					if (deltas[i] > bestVal) {
						bestVal = deltas[i];
						bestIdx = i;
					}
				}
				axisIndex = bestIdx;
				el.axisSelect.value = String(axisIndex);
				saveSettings();
				el.btnDetectAxis.textContent = 'Detect Brake Axis';
				axisDetecting = false;
			}
		}
		requestAnimationFrame(frame);
	}

	// Monitoring loop
	function tick() {
		const raw = getRawAxisValue();
		if (raw !== null) {
			const pctVal = toCalibratedPercent(raw);
			el.rawValue.textContent = raw.toFixed(3);
			el.calibratedPct.textContent = `${pctVal.toFixed(1)}%`;
			el.gaugeFill.style.width = `${pctVal}%`;
			el.gaugeLabel.textContent = `${pctVal.toFixed(0)}%`;

			if (monitoring) {
				const threshold = settings.targetPct;
				const releaseThreshold = Math.max(0, threshold - settings.hysteresisPct);
				if (settings.mode === 'oneshot') {
					if (pctVal < releaseThreshold) {
						armedForOneShot = true;
					}
					if (armedForOneShot && pctVal >= threshold) {
						oneShotBeep();
						armedForOneShot = false;
					}
				} else {
					if (pctVal >= threshold) {
						ensureContinuousTone(true);
					} else if (pctVal < releaseThreshold) {
						ensureContinuousTone(false);
					}
				}
			}
		}
		rafId = requestAnimationFrame(tick);
	}

	function startMonitoring() {
		if (!getActiveGamepad()) return;
		initAudioContext();
		monitoring = true;
		armedForOneShot = true;
		updateStartStopButtons();
	}

	function stopMonitoring() {
		monitoring = false;
		ensureContinuousTone(false);
		updateStartStopButtons();
	}

	// Bind UI controls
	function bindControls() {
		el.btnDetectAxis.addEventListener('click', detectAxis);
		el.btnSetReleased.addEventListener('click', () => {
			const raw = getRawAxisValue();
			if (raw === null) return;
			calibrationMin = raw;
			el.minVal.textContent = calibrationMin.toFixed(3);
			saveSettings();
		});
		el.btnSetPressed.addEventListener('click', () => {
			const raw = getRawAxisValue();
			if (raw === null) return;
			calibrationMax = raw;
			el.maxVal.textContent = calibrationMax.toFixed(3);
			saveSettings();
		});
		el.btnStart.addEventListener('click', startMonitoring);
		el.btnStop.addEventListener('click', stopMonitoring);
	}

	// Init
	function init() {
		loadSettings();
		updateUIFromSettings();
		bindSettingInputs();
		bindControls();

		// Gamepad events
		window.addEventListener('gamepadconnected', (e) => {
			onGamepadConnected(e.gamepad);
		});
		window.addEventListener('gamepaddisconnected', () => {
			onGamepadDisconnected();
		});

		// Poll in case the browser doesn't fire connect events until interaction
		function tryDetect() {
			const gps = navigator.getGamepads ? navigator.getGamepads() : [];
			for (const gp of gps) {
				if (gp && gp.connected) {
					onGamepadConnected(gp);
					break;
				}
			}
		}
		tryDetect();

		// Start render loop
		rafId = requestAnimationFrame(tick);

		// Initial status
		if (!getActiveGamepad()) {
			el.gpStatus.textContent = 'Waiting for connection…';
		}
		updateStartStopButtons();
	}

	document.addEventListener('visibilitychange', () => {
		// Suspend continuous tone if tab hidden
		if (document.hidden) ensureContinuousTone(false);
	});

	window.addEventListener('beforeunload', () => {
		cancelAnimationFrame(rafId);
		ensureContinuousTone(false);
	});

	// Kick off
	init();
})(); 


